import { type ReactNode, useEffect, useRef, useState } from "react";
import { useQuestionWrites, useQuestions } from "@/hooks/use-questions";
import { ApiError } from "@/services/api-client";
import type { QuestionTrigger } from "@/services/questions";
import { QuestionCard, type QuestionTopic } from "./QuestionCard";

/** What a commit's button says while Praxis reads whether it has questions. */
export const CHECKING_FOR_QUESTIONS = "Checking for questions…";

/**
 * How long a commit stays ready, over the same records, before Praxis asks its
 * questions ahead of the press. Confirming a run of records one after another
 * moves them each time, so it asks once, after the last.
 */
export const PREPARE_AFTER_MS = 8000;

/**
 * A commit that asks first: the stage's own commit, with Praxis's questions in
 * front of it.
 *
 * A press asks at the trigger; the worker reads the confirmed records and the
 * progress stream says when it is done. With nothing to ask, a stranded ask or
 * a pass that failed, the commit goes ahead with no surface. With questions,
 * the card takes the footer's place and its own button is the commit (lane L
 * decision 32). An open queue is the server's, so the card is back whenever
 * the attorney returns to the page, at the question they left.
 *
 * Once the commit is `ready` and its records, as `reading` names them, have
 * not moved for a while, Praxis asks ahead of the press, so the press usually
 * finds the questions asked and does not wait for them.
 */
export function useQuestionGate({
  taskId,
  trigger,
  topic,
  cta,
  commit,
  ready = false,
  reading = "",
}: {
  taskId: string;
  trigger: QuestionTrigger;
  /** What the answers help Praxis understand, as the card's lede says it. */
  topic: QuestionTopic;
  /** The commit's own words, which the card's button keeps. */
  cta: string;
  commit: () => void;
  /** The commit can be pressed now. */
  ready?: boolean;
  /** Changes whenever the records the trigger reads change. */
  reading?: string;
}): { press: () => void; asking: boolean; card: ReactNode } {
  const questions = useQuestions(taskId);
  const { ask, prepare } = useQuestionWrites(taskId);
  // The queue this press opened: a queue left ended by an earlier press says nothing of this one.
  const [asked, setAsked] = useState<string | null>(null);
  const queue = questions.data?.queues.find((q) => q.trigger_point === trigger);
  const goesAhead =
    queue !== undefined &&
    queue.queue_id === asked &&
    (queue.status === "ended" || (queue.status === "asking" && queue.stale));

  // The queue this press went ahead on, so a re-render never commits it twice.
  const spentRef = useRef<string | null>(null);
  useEffect(() => {
    if (!goesAhead || spentRef.current === asked) return;
    spentRef.current = asked;
    commit();
  }, [goesAhead, asked, commit]);

  const press = () => {
    ask.mutate(trigger, {
      onSuccess: (store) => {
        const opened = store.queues.find((q) => q.trigger_point === trigger);
        setAsked(opened?.queue_id ?? null);
      },
      onError: (error) => {
        // The trigger's questions are already open: read them, and the card shows them.
        if (error instanceof ApiError && error.status === 409) {
          void questions.refetch();
          return;
        }
        // An ask the server could not take holds nothing: the commit goes ahead (AC9).
        commit();
      },
    });
  };

  const open = queue?.status === "open" && questions.data !== undefined;
  const askAhead = prepare.mutate;
  const idle = !open && asked === null;
  useEffect(() => {
    if (!ready || !idle || taskId === "") return;
    const timer = setTimeout(() => {
      askAhead(trigger);
    }, PREPARE_AFTER_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [ready, idle, reading, taskId, trigger, askAhead]);
  return {
    press,
    asking: !open && (ask.isPending || (asked !== null && !goesAhead)),
    card: open ? (
      <QuestionCard
        taskId={taskId}
        store={questions.data}
        queue={queue}
        topic={topic}
        cta={cta}
        onCleared={() => {
          setAsked(null);
          commit();
        }}
      />
    ) : null,
  };
}
